// Forum appearance themes — per-user preference. DEFAULT is "olive" (the Olive/Lime look), so
// nothing changes for existing members until they pick something in the Forum "Preferences" panel.
//
// Matches the approved mockup (forumthemesettingsmockup_1.html): the forum background is a procedural
// stack — a dark top gradient + a repeating TOPO contour SVG + a repeating multicam CAMO SVG. Each of
// the 5 presets ships its OWN pre-tinted art (see forumThemeArt.js). A CUSTOM accent re-tints the base
// OLIVE art with a CSS hue-rotate + saturate filter, and colors the accent UI with the literal hex.
import { FORUM_ART } from "@/lib/forumThemeArt";

// Hue of the olive/lime base (#cfe86b) — custom themes hue-rotate the base art relative to this.
const BASE_HUE = 72;

// Neutral chrome text — constant across every theme (matches the mockup preview).
const TEXT = "#f3f3f0";
const TEXT_MUTED = "#9a9c9f";

export const FORUM_PRESETS = {
  olive: {
    key: "olive", label: "Olive / Lime", sub: "Default", swatch: "#cfe86b",
    accent: "#cfe86b", accentDim: "#a9c25a", pinnedBg: "#8a9a3a", pinnedText: "#0a0d05",
    camo: FORUM_ART.olive.camo, topo: FORUM_ART.olive.topo, patternFilter: "none",
  },
  ranger: {
    key: "ranger", label: "Ranger Green", sub: "Muted, milspec", swatch: "#9fc98a",
    accent: "#9fc98a", accentDim: "#6f9260", pinnedBg: "#5c7a52", pinnedText: "#eef5e9",
    camo: FORUM_ART.ranger.camo, topo: FORUM_ART.ranger.topo, patternFilter: "none",
  },
  coyote: {
    key: "coyote", label: "Coyote Tan", sub: "Desert multicam", swatch: "#e0b464",
    accent: "#e0b464", accentDim: "#b6893f", pinnedBg: "#a9793a", pinnedText: "#150f08",
    camo: FORUM_ART.coyote.camo, topo: FORUM_ART.coyote.topo, patternFilter: "none",
  },
  gunmetal: {
    key: "gunmetal", label: "Gunmetal Slate", sub: "Cool tactical", swatch: "#8fc7d9",
    accent: "#8fc7d9", accentDim: "#5f92a3", pinnedBg: "#4d6f7d", pinnedText: "#eaf4f7",
    camo: FORUM_ART.gunmetal.camo, topo: FORUM_ART.gunmetal.topo, patternFilter: "none",
  },
  blackout: {
    key: "blackout", label: "Blackout", sub: "Minimal, one accent", swatch: "#C2600F",
    accent: "#C2600F", accentDim: "#8f480c", pinnedBg: "#8f480c", pinnedText: "#f2ede4",
    camo: FORUM_ART.blackout.camo, topo: FORUM_ART.blackout.topo, patternFilter: "none",
  },
};

export const FORUM_PRESET_ORDER = ["olive", "ranger", "coyote", "gunmetal", "blackout"];

// 12 quick-pick accent colors (matching the mockup's color-dot row).
export const FORUM_QUICK_COLORS = [
  { name: "red", hex: "#e0574f" }, { name: "orange", hex: "#e0824f" },
  { name: "amber", hex: "#e0b464" }, { name: "yellow", hex: "#d9d15c" },
  { name: "lime", hex: "#cfe86b" }, { name: "green", hex: "#7fd18a" },
  { name: "teal", hex: "#5fd0c2" }, { name: "sky", hex: "#5fb8e0" },
  { name: "blue", hex: "#6f8fe0" }, { name: "violet", hex: "#a97fe0" },
  { name: "magenta", hex: "#e05fd0" }, { name: "rose", hex: "#e0a4c2" },
];

export function isValidHex(hex) {
  return typeof hex === "string" && /^#[0-9a-fA-F]{6}$/.test(hex.trim());
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function hexHue(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d === 0) return 0;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = Math.round(h * 60);
  return h < 0 ? h + 360 : h;
}

// Dark or light text that reads on top of the accent fill.
export function textOn(hex) {
  const [r, g, b] = hexToRgb(hex);
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 140 ? "#0a0d05" : "#f4f6ee";
}

// hex + alpha (0..1) -> rgba() string.
export function hexA(hex, a) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Build a theme object from a stored selection (used by the Forum page + the Preferences preview).
export function themeFromSelection(themeKey, accentHex) {
  if (themeKey === "custom" && isValidHex(accentHex)) {
    const hex = accentHex.trim();
    const delta = hexHue(hex) - BASE_HUE;
    const base = FORUM_PRESETS.olive;
    return {
      key: "custom", label: "Custom", sub: "Your accent", swatch: hex,
      accent: hex, accentDim: hex, pinnedBg: hex, pinnedText: textOn(hex),
      camo: base.camo, topo: base.topo,
      patternFilter: `hue-rotate(${delta}deg) saturate(1.05)`,
    };
  }
  return FORUM_PRESETS[themeKey] || FORUM_PRESETS.olive;
}

// Resolve the signed-in user's forum theme (defaults to olive for logged-out / unset).
export function resolveForumTheme(user) {
  if (!user) return FORUM_PRESETS.olive;
  return themeFromSelection(user.forum_theme || "olive", user.forum_accent_hex);
}

// Procedural forum background — kept subtle so it stays in line with the site-wide near-black
// (#050506) palette: a heavy dark wash sits over a faint repeating topo contour + multicam camo,
// so each preset reads as a dark textured field (not a bright olive block). The hue-rotate/saturate
// filter (custom accents only) is applied to the whole pattern stack.
export function buildForumBg(theme) {
  return {
    backgroundColor: "#050506",
    backgroundImage:
      `linear-gradient(180deg, rgba(5,6,7,0.9), rgba(5,6,7,0.94)), url(${theme.topo}), url(${theme.camo})`,
    backgroundRepeat: "no-repeat, repeat, repeat",
    backgroundSize: "cover, 1000px 1000px, 620px 620px",
    filter: theme.patternFilter || "none",
  };
}

// Map a theme object to the Forum page's PALETTE token names. Accent-derived tokens computed here;
// pinned badge uses the preset's own pinned color (per the approved mockup); text chrome is neutral.
export function buildForumPalette(theme) {
  const accent = theme.accent;
  return {
    text: TEXT,
    textMuted: TEXT_MUTED,
    accent,
    accentText: textOn(accent),
    lime: accent,
    darkGreen: textOn(accent),
    // Pinned badge — colored per preset (was fixed gold before the mockup).
    gold: theme.pinnedBg,
    goldText: theme.pinnedText,
    hotBg: "rgba(194,96,15,0.85)",
    hotText: "#ffffff",
    tagLimeText: accent,
    tagLimeBg: hexA(accent, 0.1),
    tagLimeBorder: hexA(accent, 0.45),
    // Category tabs — active fills with the accent; inactive is translucent black over the camo.
    chipActiveBg: accent,
    chipActiveText: textOn(accent),
    chipText: "#d8dad4",
    chipBorder: hexA(accent, 0.22),
    chipBg: "rgba(0,0,0,0.25)",
    hairline: hexA(accent, 0.12),
    // accent + '38' (~0.22 alpha) — the mockup's --p-border.
    cardBorder: hexA(accent, 0.22),
    // Thread rows are pure black over the camo (per the mockup).
    rowSurface: "#000000",
  };
}
